/* Masthead particle overlay. Renders two stacked transparent canvases
   atop the masthead image:

     - Smoke layer (always alive while the page is mounted): runs the
       sine-modulated breathing loop. On win, breathing pauses but the
       container stays alive so existing smoke particles drift up and
       fade naturally instead of being snapped out of existence.
     - Fire layer (created on demand): the dragon's fire-breath. Born
       at the start of the fire phase, destroyed once particles have
       finished fading.

   Phase progression on win (or masthead click):

     playing   → smoke breathing
     win lead  → smoke keeps breathing (~2 s) so confetti has solo time
     fire      → smoke breathing OFF (existing particles drift), fire
                 layer alive
     post-fire → fire layer destroyed, smoke breathing resumes

   Lifecycle is driven by Preact signals, not by props, so the component
   mounts once at the top of the tree and reacts to game state directly. */

import { effect } from "@preact/signals";
import { useEffect, useRef } from "preact/hooks";
import type { Container } from "@tsparticles/engine";
import * as gameState from "../game/state";
import { celebrationTrigger, status } from "../game/state";
import {
  BREATHING_INHALE_OFFSET_S,
  type BreathingHandle,
  startBreathing,
} from "./breathing";
import {
  prefersReducedMotion,
  prepareEngine,
  tsParticles,
} from "./engine";
import { type FireBreathHandle, startFireBreath } from "./fireBreath";
import {
  IDLE_OPTIONS,
  SMOKE_OPTIONS,
  WIN_OPTIONS,
  WIN_SEQUENCE_BREATH_IN_MS,
  WIN_SEQUENCE_FIRE_LEAD_MS,
  WIN_SEQUENCE_FIRE_MS,
  WIN_SEQUENCE_TOTAL_MS,
} from "./mastheadConfigs";

const SMOKE_CONTAINER_ID = "palindragon-masthead-smoke";
const FIRE_CONTAINER_ID = "palindragon-masthead-fire";

type Phase = "idle" | "smoke" | "fire";

/** Background-state-appropriate options + whether breathing should run. */
function backgroundFor(s: typeof status.value): {
  options: typeof SMOKE_OPTIONS;
  breathe: boolean;
} {
  return s === "lost"
    ? { options: IDLE_OPTIONS, breathe: false }
    : { options: SMOKE_OPTIONS, breathe: true };
}

export function MastheadParticles() {
  const hostRef = useRef<HTMLDivElement>(null);
  const smokeHostRef = useRef<HTMLDivElement>(null);
  const fireHostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (prefersReducedMotion()) return;

    let cancelled = false;
    const timeouts: number[] = [];
    let stopSignalEffect: (() => void) | null = null;
    let smokeContainer: Container | null = null;
    let fireContainer: Container | null = null;
    let breathing: BreathingHandle | null = null;
    let fireBreath: FireBreathHandle | null = null;
    /** True from the moment a win sequence is scheduled until endFire
       runs. Used so a "won → playing" transition mid-celebration (e.g.
       Enter pressed twice) doesn't tear down the fire before it starts. */
    let winSequenceInFlight = false;

    const setPhase = (phase: Phase): void => {
      hostRef.current?.setAttribute("data-phase", phase);
    };

    /** Replace the smoke layer container (used on game-state changes:
       playing/won → smoke, lost → idle). Stops the breathing loop;
       restarts it iff the new options call for it. */
    const reloadSmoke = async (
      options: typeof SMOKE_OPTIONS,
      breathe: boolean,
    ): Promise<void> => {
      if (cancelled || !smokeHostRef.current) return;
      breathing?.stop();
      breathing = null;
      smokeContainer?.destroy();
      smokeContainer = null;
      const next = await tsParticles.load({
        id: SMOKE_CONTAINER_ID,
        element: smokeHostRef.current,
        options,
      });
      if (cancelled) {
        next?.destroy();
        return;
      }
      smokeContainer = next ?? null;
      if (breathe && smokeContainer) {
        breathing = startBreathing(smokeContainer);
      }
    };

    /** Begin the fire phase: pause breathing (existing smoke particles
       continue their natural drift + fade), spawn the fire container in
       the parallel layer above the smoke, and kick off the fireBreath
       RAF spawner whose intensity envelope holds at full strength
       through most of the breath, then smoothly tails off across the
       final FIRE_TAIL_OFF_MS. */
    const startFire = async (): Promise<void> => {
      if (cancelled || !fireHostRef.current) return;
      breathing?.stop();
      breathing = null;
      fireBreath?.stop();
      fireBreath = null;
      fireContainer?.destroy();
      fireContainer = null;
      setPhase("fire");
      const next = await tsParticles.load({
        id: FIRE_CONTAINER_ID,
        element: fireHostRef.current,
        options: WIN_OPTIONS,
      });
      if (cancelled) {
        next?.destroy();
        return;
      }
      fireContainer = next ?? null;
      if (fireContainer) {
        fireBreath = startFireBreath(fireContainer, {
          delayMs: WIN_SEQUENCE_BREATH_IN_MS,
          durationMs: WIN_SEQUENCE_FIRE_MS,
        });
      }
    };

    /** End the fire phase: destroy the fire container and resume
       breathing on the still-alive smoke container if game state
       still calls for smoke. The breathing cycle is restarted at
       BREATHING_INHALE_OFFSET_S — i.e., the start of the inhale half —
       so the dragon spends a full quiescent half-cycle "catching its
       breath" after the fire before any new smoke appears. */
    const endFire = (): void => {
      winSequenceInFlight = false;
      fireBreath?.stop();
      fireBreath = null;
      fireContainer?.destroy();
      fireContainer = null;
      const bg = backgroundFor(status.value);
      setPhase(bg.breathe ? "smoke" : "idle");
      if (bg.breathe && smokeContainer && !breathing) {
        breathing = startBreathing(smokeContainer, {
          phaseOffsetS: BREATHING_INHALE_OFFSET_S,
        });
      }
    };

    const runWinSequence = () => {
      timeouts.forEach(clearTimeout);
      timeouts.length = 0;
      winSequenceInFlight = true;
      // Lead-in: smoke keeps breathing while the confetti (fired by
      // BoardEffects on the same trigger) launches solo from the
      // bottom corners. After the lead, swap to fire and schedule the
      // fire teardown far enough out that the longest in-flight fire
      // particle (~0.7 s opacity-fade) finishes before the container
      // is destroyed. 1200 ms buffer gives clean fade plus headroom.
      timeouts.push(
        window.setTimeout(() => {
          void startFire();
          timeouts.push(
            window.setTimeout(endFire, WIN_SEQUENCE_TOTAL_MS + 1200),
          );
        }, WIN_SEQUENCE_FIRE_LEAD_MS),
      );
    };

    (async () => {
      await prepareEngine();
      if (cancelled) return;
      const initial = backgroundFor(status.value);
      setPhase(initial.breathe ? "smoke" : "idle");
      await reloadSmoke(initial.options, initial.breathe);

      let prev = status.value;
      let prevTrigger = celebrationTrigger.value;
      stopSignalEffect = effect(() => {
        // Manual trigger (masthead click) — run the win sequence
        // regardless of game state. Checked first so an in-flight game
        // transition can't mask a click.
        const trigger = celebrationTrigger.value;
        if (trigger !== prevTrigger) {
          prevTrigger = trigger;
          runWinSequence();
          return;
        }

        const curr = status.value;
        if (curr === prev) return;
        prev = curr;

        if (curr === "playing") {
          // If a win celebration is in flight (scheduled but not torn
          // down yet), let it run to completion. Pressing Enter
          // immediately after winning fires newGame() and lands us
          // here within the 2-second confetti-lead window — tearing
          // the sequence down here would cancel the fire entirely.
          // endFire restores breathing when it runs, and smoke options
          // don't differ between "playing" and "won", so there is
          // nothing else to reset.
          if (winSequenceInFlight) return;
          fireBreath?.stop();
          fireBreath = null;
          fireContainer?.destroy();
          fireContainer = null;
          setPhase("smoke");
          void reloadSmoke(SMOKE_OPTIONS, true);
          return;
        }
        if (curr === "lost") {
          timeouts.forEach(clearTimeout);
          timeouts.length = 0;
          winSequenceInFlight = false;
          fireBreath?.stop();
          fireBreath = null;
          fireContainer?.destroy();
          fireContainer = null;
          setPhase("idle");
          void reloadSmoke(IDLE_OPTIONS, false);
          return;
        }
        // curr === "won"
        // Restoring a saved playthrough flips status playing → won
        // inside a batched write. Suppress the celebration — we only
        // want fire-breath on the live moment of victory. Smoke options
        // are the same for "playing" and "won", so skipping the
        // sequence leaves the masthead in the right idle state.
        if (gameState.isHydrating) return;
        runWinSequence();
      });
    })();

    return () => {
      cancelled = true;
      timeouts.forEach(clearTimeout);
      stopSignalEffect?.();
      breathing?.stop();
      breathing = null;
      fireBreath?.stop();
      fireBreath = null;
      smokeContainer?.destroy();
      smokeContainer = null;
      fireContainer?.destroy();
      fireContainer = null;
    };
  }, []);

  return (
    <div ref={hostRef} class="masthead-particles" aria-hidden="true">
      <div ref={smokeHostRef} class="masthead-particles__layer" />
      <div ref={fireHostRef} class="masthead-particles__layer" />
    </div>
  );
}
